/**
 * Smart Project Copy Types
 *
 * Type definitions for Best Practice clash detection,
 * application workflows, and sync states.
 */

/**
 * Clash filter status for filtering active vs resolved clashes
 */
export type ClashFilterStatus = 'active' | 'resolved'

/**
 * Best Practice Clash entity
 */
export interface BestPracticeClash {
    id: string
    projectId: string
    bestPracticeId: string
    familyNumber: string
    productFamilyId: string
    productFamilyName: string
    errorMessage: string
    isResolved: boolean
    resolvedDate?: string | null
    resolvedBy?: string | null
    projectElementNodeId: string
    treePath: string
    contentTextPreview?: string | null
    metadata: {
        created: string
        createdBy?: string | null
        modified?: string | null
        modifiedBy?: string | null
    }
}

/**
 * Input for fetching Best Practice clashes
 */
export interface GetBestPracticeClashesInput {
    projectId: string
    treePath?: string
}

/**
 * Response for Best Practice clashes query
 */
export interface GetBestPracticeClashesResponse {
    bestPracticeClashes: BestPracticeClash[]
}

/**
 * Input for getting clash count
 */
export interface GetBestPracticeClashCountInput {
    projectId: string
    treePath?: string
}

/**
 * Response for clash count query
 */
export interface GetBestPracticeClashCountResponse {
    bestPracticeClashCount: number
}

/**
 * Input for resolving a clash
 */
export interface ResolveBestPracticeClashInput {
    clashId: string
}

/**
 * Response for resolve clash mutation
 */
export interface ResolveBestPracticeClashResponse {
    resolveBestPracticeClash: {
        id: string
        isResolved: boolean
        resolvedDate: string
        resolvedBy: string
    }
}

/**
 * Best Practice Sync Action enum
 */
export enum BestPracticeSyncAction {
    Null = 'Null',
    None = 'NONE',
    SyncFromBP = 'SYNCFROMBP',
    CreateNewBP = 'CREATENEWBP',
}

/**
 * Sync status result from backend
 */
export interface SyncActionResult {
    action: BestPracticeSyncAction
    message: string
    latestVersionId: string
    currentVersionId: string
    isUserAddedContentBestPractice: boolean
}

/**
 * Best Practice application status
 */
export interface BPApplicationStatus {
    state: 'IDLE' | 'APPLYING' | 'SUCCESS' | 'ERROR' | 'CLASHES_DETECTED'
    progress: number
    message: string
    error?: string | null
}

/**
 * Best Practice application progress update
 */
export interface BPApplicationProgressUpdate {
    progress: number
    message: string
    elementId?: string
    familyName?: string
}

/**
 * Input for applying Best Practices to a project
 */
export interface ApplyBestPracticesInput {
    projectId: string
    sourceProjectId: string
    treePath?: string
}

/**
 * Response for apply Best Practices mutation
 */
export interface ApplyBestPracticesResponse {
    applyBestPractices: {
        success: boolean
        message: string
        clashesDetected: number
    }
}

/**
 * Hook result for clash detection
 */
export interface UseBestPracticeClashDetectionResult {
    clashCount: number
    showWarning: boolean
    setShowWarning: (show: boolean) => void
    loading: boolean
    error: import('@apollo/client').ApolloError | null
    checkForClashes: () => void
    resetClashState: () => void
}

/**
 * Hook result for Best Practice application
 */
export interface UseBPApplicationResult {
    applicationStatus: BPApplicationStatus
    applyBestPractices: (input: ApplyBestPracticesInput) => Promise<void>
    resetApplication: () => void
    isApplying: boolean
}
